import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Chip,
  CircularProgress,
  Button,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Container,
  Badge,
  Autocomplete,
  Pagination,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import { useNavigate } from 'react-router-dom';
// hooks
import useAuth from '../../../../hooks/useAuth';
import {
  getTradeAlerts,
  setSelectedAlert,
  clearTradeAlerts,
  getUsersByRole,
  getAlertTypes,
  getLastSuspiciousTradeUpdated,
} from '../../../../redux/slices/suspiciousActivity';
import { getClientProfileWithTradeRoute } from '../../../../utils/rediretionroutes';
import { fCompactDateTime } from '../../../../utils/formatTime';

// Indian currency formatter
const fIndianCurrency = (number) => {
  if (number === null || number === undefined || Number.isNaN(number)) return '₹0';
  return `${Math.round(number).toLocaleString('en-IN')}`;
};

// Styled components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  width: '100%',
  overflowX: 'auto',
  '& .MuiTable-root': {
    minWidth: 1200, // Ensure table has minimum width to trigger horizontal scroll
    '& .MuiTableCell-root': {
      border: `1px solid ${theme.palette.divider}`, // Add border to all cells
      borderTop: 'none', // Remove top border to avoid double borders
      borderLeft: 'none', // Remove left border to avoid double borders
      '&:first-of-type': {
        borderLeft: `1px solid ${theme.palette.divider}`, // Add left border to first column
      },
    },
  },
  '& .MuiTableHead-root': {
    position: 'sticky',
    top: 0,

    '& .MuiTableRow-root': {
      '& .MuiTableCell-root': {
        backgroundColor: theme.palette.background.neutral,
        fontWeight: 600,
        position: 'sticky',
        top: 0,
        whiteSpace: 'nowrap',
        borderTop: `1px solid ${theme.palette.divider}`, // Add top border to header
      },
      '& .MuiTableCell-root.sticky-actions': {
        position: 'sticky',
        right: 0,
        top: 0,
        backgroundColor: theme.palette.background.neutral,
        borderLeft: `1px solid ${theme.palette.divider}`,
        zIndex: 2, // Higher z-index for sticky header actions
      },
    },
  },
  '& .MuiTableBody-root': {
    '& .MuiTableRow-root': {
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      '& .MuiTableCell-root': {
        whiteSpace: 'nowrap',
      },
      '& .MuiTableCell-root.sticky-actions': {
        position: 'sticky',
        right: 0,
        backgroundColor: theme.palette.background.paper,
        borderLeft: `1px solid ${theme.palette.divider}`,
        zIndex: 1,
      },
      '&:hover .MuiTableCell-root.sticky-actions': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  },
}));

const AlertTypeChip = styled(Chip)(({ alerttype }) => {
  const getColor = () => {
    switch (alerttype) {
      case 'WASH_TRADE':
        return { backgroundColor: '#ffebee', color: '#c62828', borderColor: '#c62828' };
      case 'SPOOFING_BID':
        return { backgroundColor: '#fff3e0', color: '#ef6c00', borderColor: '#ef6c00' };
      case 'SPOOFING_ASK':
        return { backgroundColor: '#f3e5f5', color: '#7b1fa2', borderColor: '#7b1fa2' };
      case 'LAYERING':
        return { backgroundColor: '#e8f5e8', color: '#2e7d32', borderColor: '#2e7d32' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#616161', borderColor: '#616161' };
    }
  };

  return {
    ...getColor(),
    border: `1px solid`,
    fontWeight: 600,
    fontSize: '0.75rem',
  };
});

const SeverityChip = styled(Chip)(({ severity }) => {
  const getColor = () => {
    switch (severity) {
      case 'high':
        return { backgroundColor: '#ffebee', color: '#c62828' };
      case 'medium':
        return { backgroundColor: '#fff3e0', color: '#ef6c00' };
      case 'low':
        return { backgroundColor: '#e8f5e8', color: '#2e7d32' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#616161' };
    }
  };

  return {
    ...getColor(),
    fontWeight: 600,
    fontSize: '0.75rem',
  };
});

const DetailCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
}));

const TRADE_TYPE_OPTIONS = [
  { label: 'OPEN', value: 'OPEN' },
  { label: 'CLOSED', value: 'ORDER' },
  { label: 'LIMIT', value: 'PENDING' },
];

// const MENU_ITEM_HEIGHT = 48;
// const MENU_ITEM_PADDING_TOP = 8;
// const MenuProps = {
//   PaperProps: {
//     style: {
//       maxHeight: MENU_ITEM_HEIGHT * 4.5 + MENU_ITEM_PADDING_TOP,
//       width: 250,
//     },
//   },
// };

// Function to get trade type label
const getTradeTypeLabel = (tradeType) => {
  switch (tradeType) {
    case 'OPEN':
      return 'OPEN';
    case 'CLOSE':
      return 'CLOSE';
    case 'ORDER':
      return 'CLOSE';
    case 'EXECUTED':
      return 'PASSED LIMIT';
    case 'PENDING':
      return 'LIMIT';
    case 'CANCELLED':
      return 'CANCELLED LIMIT';
    default:
      return tradeType || 'UNKNOWN';
  }
};

// Function to get trade type chip color
const getTradeTypeColor = (tradeType) => {
  switch (tradeType) {
    case 'OPEN':
      return 'primary';
    case 'CLOSE':
      return 'info';
    case 'ORDER':
      return 'info';
    case 'EXECUTED':
      return 'warning';
    case 'PENDING':
      return 'default';
    case 'CANCELLED':
      return 'error';
    default:
      return 'default';
  }
};

// Alert Types Modal Component
function AlertTypesModal({ open, onClose, alertTypes }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontSize="1rem">
            Alert Types
          </Typography>
          <IconButton onClick={onClose} size="small">
            <ClearIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
          {alertTypes?.map((alertType, index) => (
            <AlertTypeChip
              key={index}
              label={alertType.replace(/_/g, ' ')}
              alerttype={alertType}
              size="small"
              sx={{ fontSize: '0.7rem', height: '24px' }}
            />
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ py: 1 }}>
        <Button onClick={onClose} variant="contained" size="small">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AlertTypesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  alertTypes: PropTypes.array,
};

// Applied Filters Modal Component

// Convert minutes to DD HH MM format
const formatDuration = (minutes) => {
  if (!minutes || Number.isNaN(minutes)) return '-';
  const absMin = Math.abs(minutes);
  const days = Math.floor(absMin / 1440);
  const hours = Math.floor((absMin % 1440) / 60);
  const mins = Math.floor(absMin % 60);
  let result = '';
  if (days > 0) result += `${days}d `;
  if (hours > 0 || days > 0) result += `${hours}h `;
  result += `${mins}m`;
  return result.trim();
};

// Alert Detail Dialog Component
function AlertDetailDialog({ open, onClose, alert }) {
  if (!alert) return null;

  const { trade, user, alerts } = alert;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' },
      }}
    >
      {' '}
      <DialogTitle>
        <Typography variant="h5" component="div">
          Trade Alert Details - {trade?.script?.name} - {user.username}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          Latest Alert: {fCompactDateTime(alert.latestAlertTimestamp)}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <DetailCard>
            <Typography variant="h6" gutterBottom color="primary">
              Trade Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12}>
                <Box sx={{ width: '100%' }}>
                  <Grid container spacing={1}>
                    {/* Row 1: User / Script */}
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        User
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2">{user?.username}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user?.firstName} {user?.lastName}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Script
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" fontWeight={600}>
                        {trade?.script?.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {trade?.script?.tradingsymbol} • {trade?.script?.exchange?.name}
                      </Typography>
                    </Grid>
                    {/* Row 2: Trade Side / Trade Type */}
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Trade Side
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Chip label={trade?.side} color={trade?.side === 'BUY' ? 'success' : 'error'} size="small" />
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Trade Type
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Chip
                        label={getTradeTypeLabel(trade?.type)}
                        color={getTradeTypeColor(trade?.type)}
                        size="small"
                      />
                    </Grid>
                    {/* Row 3: Quantity / Profit/Loss */}
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Quantity
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2">{trade?.quantity}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Profit/Loss
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color={trade?.profit >= 0 ? 'success.dark' : 'error.main'}>
                        {fIndianCurrency(trade?.profit)}
                      </Typography>
                    </Grid>
                    {/* Row 4: Buy Price / Sell Price */}
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Buy Price
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="success.dark">
                        {fIndianCurrency(trade?.buy)}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Sell Price
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="error.main">
                        {fIndianCurrency(trade?.sell)}
                      </Typography>
                    </Grid>
                    {/* Row 5: Bought At / Sold At */}
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Bought At
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">
                        {trade?.boughtAt ? fCompactDateTime(trade.boughtAt) : '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Sold At
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">
                        {trade?.soldAt ? fCompactDateTime(trade.soldAt) : '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Duration
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">
                        {trade?.type === 'ORDER' && trade?.duration != null ? formatDuration(trade.duration) : '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </DetailCard>
          {/* Individual Alert Details */}
          <DetailCard>
            <Typography variant="h6" gutterBottom color="primary">
              Alert Details ({alerts?.length || 0} alerts)
            </Typography>{' '}
            <Stack spacing={2}>
              {' '}
              {alerts?.map((alertItem, index) => {
                const alertData = alertItem?.alertData;

                return (
                  <Box
                    key={index}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      backgroundColor: index === 0 ? 'action.hover' : 'transparent',
                    }}
                  >
                    <Grid container spacing={1}>
                      {/* Row 1: Alert #/Time */}
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Alert #{index + 1} {index === 0 && '(Latest)'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">{fCompactDateTime(alertItem.alertTimestamp)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Alert Type
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <AlertTypeChip label={alertItem.alertType} alerttype={alertItem.alertType} size="small" />
                      </Grid>
                      {/* Row 2: Severity/Price */}
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Severity
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <SeverityChip
                          label={alertData?.severityLevel || 'low'}
                          severity={alertData?.severityLevel || 'low'}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Price
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">{fIndianCurrency(alertData?.price)}</Typography>
                      </Grid>
                      {/* Row 3: Volume/Direction */}
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Volume
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">{alertData?.volume?.toLocaleString()}</Typography>
                      </Grid>

                      {/* Row 4: Description/Market Context (first pair) */}
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Description
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {alertData?.description}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })}
            </Stack>
          </DetailCard>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AlertDetailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  alert: PropTypes.shape({
    _id: PropTypes.string,
    alertType: PropTypes.string,
    alertTimestamp: PropTypes.string,
    alertPrice: PropTypes.number,
    latestAlertTimestamp: PropTypes.string,
    createdAt: PropTypes.string,
    alertTypes: PropTypes.array,
    alertCount: PropTypes.number,
    alertData: PropTypes.shape({
      script: PropTypes.string,
      alertType: PropTypes.string,
      severityLevel: PropTypes.string,
      price: PropTypes.number,
      volume: PropTypes.number,
      direction: PropTypes.string,
      description: PropTypes.string,
      marketContext: PropTypes.string,
      orderBookSnapshot: PropTypes.string,
    }),
    user: PropTypes.shape({
      _id: PropTypes.string,
      username: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
    trade: PropTypes.shape({
      _id: PropTypes.string,
      type: PropTypes.string,
      side: PropTypes.string,
      quantity: PropTypes.number,
      profit: PropTypes.number,
      buy: PropTypes.number,
      sell: PropTypes.number,
      boughtAt: PropTypes.string,
      soldAt: PropTypes.string,
      duration: PropTypes.number,
      script: PropTypes.shape({
        _id: PropTypes.string,
        name: PropTypes.string,
        tradingsymbol: PropTypes.string,
        exchange: PropTypes.shape({
          name: PropTypes.string,
        }),
      }),
    }),
    alerts: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
        alertType: PropTypes.string,
        alertTimestamp: PropTypes.string,
        alertData: PropTypes.object,
      })
    ),
  }),
};

// Filter Dialog Component
function FilterDialog({ open, onClose, filters, onApply, userRole }) {
  const dispatch = useDispatch();
  const { users, usersLoaded, alertTypes } = useSelector((state) => state.suspiciousActivity);
  const [tempFilters, setTempFilters] = useState(filters);

  useEffect(() => {
    setTempFilters(filters);
  }, [filters]);

  // Load alert types when dialog opens
  useEffect(() => {
    if (open && !alertTypes.isLoaded && !alertTypes.isLoading) {
      dispatch(getAlertTypes());
    }
  }, [open, dispatch, alertTypes.isLoaded, alertTypes.isLoading]);

  // Load users when dialog opens based on user role
  useEffect(() => {
    if (open) {
      switch (userRole) {
        case 'superAdmin':
          ['admin', 'broker', 'client'].forEach((role) => {
            if (!usersLoaded[role]) {
              dispatch(getUsersByRole(role));
            }
          });
          break;
        case 'admin':
          ['broker', 'client'].forEach((role) => {
            if (!usersLoaded[role]) {
              dispatch(getUsersByRole(role));
            }
          });
          break;
        case 'broker':
          if (!usersLoaded.client) {
            dispatch(getUsersByRole('client'));
          }
          break;
        default:
          break;
      }
    }
  }, [open, dispatch, usersLoaded, userRole]);

  // Hierarchical user loading logic similar to SuspiciousActivityFilters
  useEffect(() => {
    if (open && tempFilters.admin && tempFilters.admin.length > 0) {
      dispatch(getUsersByRole('broker', { admin: tempFilters.admin }, true));
      dispatch(getUsersByRole('client', { admin: tempFilters.admin }, true));
    }
  }, [open, dispatch, tempFilters.admin]);

  useEffect(() => {
    if (open && tempFilters.broker && tempFilters.broker.length > 0) {
      const clientParams = {};
      if (tempFilters.admin && tempFilters.admin.length > 0) {
        clientParams.admin = tempFilters.admin;
      }
      clientParams.broker = tempFilters.broker;
      dispatch(getUsersByRole('client', clientParams, true));
    }
  }, [open, dispatch, tempFilters.broker, tempFilters.admin]);

  const handleChange = (field, value) => {
    setTempFilters((prev) => ({ ...prev, [field]: value }));
  };

  // Handle user filter changes with hierarchical reset logic
  const handleUserFilterChange = (role, selectedUsers) => {
    let newFilters = {
      ...tempFilters,
      [`${role}`]: selectedUsers.map((user) => user._id),
    };

    // Reset child selections when parent changes
    if (role === 'admin') {
      newFilters = { ...newFilters, broker: [], client: [] };
    } else if (role === 'broker') {
      newFilters = { ...newFilters, client: [] };
    }

    setTempFilters(newFilters);
  };

  const handleApply = () => {
    onApply(tempFilters);
    onClose();
  };
  const handleReset = () => {
    const resetFilters = {
      client: [],
      admin: [],
      broker: [],
      script: '',
      alertType: [],
      tradeType: [],
      startTime: '',
      endTime: '',
      startPrice: '',
      endPrice: '',
      duration: '',
      page: 1,
      pageSize: 20,
    };
    setTempFilters(resetFilters);
    onApply(resetFilters);
    onClose();
  };
  // Get users for each role
  const getUsers = (role) => (Array.isArray(users[role]) ? users[role] : []);

  const getSelectedUsers = (role) => {
    const roleIds = tempFilters[`${role}`] || [];
    return getUsers(role).filter((user) => roleIds.includes(user._id));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Filter Trade Alerts</Typography>
          <IconButton onClick={onClose} size="small">
            <ClearIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Time Range */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom color="primary">
              Time Range
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Start Time"
              type="datetime-local"
              value={tempFilters.startTime}
              onChange={(e) => handleChange('startTime', e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="End Time"
              type="datetime-local"
              value={tempFilters.endTime}
              onChange={(e) => handleChange('endTime', e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          {/* Price Range */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom color="primary">
              Price Range
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Start Price"
              type="number"
              value={tempFilters.startPrice}
              onChange={(e) => handleChange('startPrice', e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="End Price"
              type="number"
              value={tempFilters.endPrice}
              onChange={(e) => handleChange('endPrice', e.target.value)}
              size="small"
            />
          </Grid>
          {/* Duration Filter */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom color="primary">
              Duration
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Duration (In Minutes)"
              type="number"
              placeholder="Duration (In Minutes)"
              value={tempFilters.duration}
              onChange={(e) => {
                const { value } = e.target;
                // Only allow positive numbers
                if (value === '' || (Number(value) > 0 && Number.isInteger(Number(value)))) {
                  handleChange('duration', value);
                }
              }}
              inputProps={{
                min: 1,
                step: 1,
              }}
              size="small"
            />
          </Grid>
          {/* Script Name */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom color="primary">
              Script
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Script Name"
              value={tempFilters.script}
              onChange={(e) => handleChange('script', e.target.value)}
              placeholder="Search by script name..."
              size="small"
            />
          </Grid>
          {/* User Filters */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom color="primary">
              Users
            </Typography>
          </Grid>
          {/* Admin Filter - Only for superAdmin */}
          {userRole === 'superAdmin' && (
            <Grid item xs={12} md={4}>
              {' '}
              <Autocomplete
                multiple
                size="small"
                options={getUsers('admin')}
                value={getSelectedUsers('admin')}
                onChange={(event, newValue) => handleUserFilterChange('admin', newValue)}
                getOptionLabel={(option) => {
                  const name = `${option.firstname || ''} ${option.lastname || ''}`.trim();
                  return name ? `${name} (${option.username})` : option.username;
                }}
                filterOptions={(options, { inputValue }) =>
                  options.filter((option) => {
                    const fullName = `${option.firstname || ''} ${option.lastname || ''}`.toLowerCase();
                    const username = (option.username || '').toLowerCase();
                    const searchTerm = inputValue.toLowerCase();
                    return fullName.includes(searchTerm) || username.includes(searchTerm);
                  })
                }
                isOptionEqualToValue={(option, value) => option._id === value._id}
                renderInput={(params) => (
                  <TextField {...params} label="Select Admins" placeholder="Search by name or username..." />
                )}
                renderTags={(tagValue, getTagProps) =>
                  tagValue
                    .map((option, index) => {
                      const name = `${option.firstname || ''} ${option.lastname || ''}`.trim();
                      const label = name ? `${name} (${option.username})` : option.username;
                      return (
                        <Chip
                          {...getTagProps({ index })}
                          key={option._id}
                          label={label}
                          size="small"
                          variant="outlined"
                        />
                      );
                    })
                    .concat(
                      tagValue.length > 2
                        ? [
                            <Chip
                              key="admin-more"
                              label={`+${tagValue.length - 2} more`}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />,
                          ]
                        : []
                    )
                }
              />
            </Grid>
          )}
          {/* Broker Filter - For superAdmin and admin */}
          {(userRole === 'superAdmin' || userRole === 'admin') && (
            <Grid item xs={12} md={4}>
              {' '}
              <Autocomplete
                multiple
                size="small"
                options={getUsers('broker')}
                value={getSelectedUsers('broker')}
                onChange={(event, newValue) => handleUserFilterChange('broker', newValue)}
                getOptionLabel={(option) => {
                  const name = `${option.firstname || ''} ${option.lastname || ''}`.trim();
                  return name ? `${name} (${option.username})` : option.username;
                }}
                filterOptions={(options, { inputValue }) =>
                  options.filter((option) => {
                    const fullName = `${option.firstname || ''} ${option.lastname || ''}`.toLowerCase();
                    const username = (option.username || '').toLowerCase();
                    const searchTerm = inputValue.toLowerCase();
                    return fullName.includes(searchTerm) || username.includes(searchTerm);
                  })
                }
                isOptionEqualToValue={(option, value) => option._id === value._id}
                renderInput={(params) => (
                  <TextField {...params} label="Select Brokers" placeholder="Search by name or username..." />
                )}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => {
                    const name = `${option.firstname || ''} ${option.lastname || ''}`.trim();
                    const label = name ? `${name} (${option.username})` : option.username;
                    return (
                      <Chip
                        {...getTagProps({ index })}
                        key={option._id}
                        label={label}
                        size="small"
                        variant="outlined"
                      />
                    );
                  })
                }
              />
            </Grid>
          )}
          {/* Client Filter - For all roles */}
          <Grid item xs={12} md={4}>
            {' '}
            <Autocomplete
              multiple
              size="small"
              options={getUsers('client')}
              value={getSelectedUsers('client')}
              onChange={(event, newValue) => handleUserFilterChange('client', newValue)}
              getOptionLabel={(option) => {
                const name = `${option.firstname || ''} ${option.lastname || ''}`.trim();
                return name ? `${name} (${option.username})` : option.username;
              }}
              filterOptions={(options, { inputValue }) =>
                options.filter((option) => {
                  const fullName = `${option.firstname || ''} ${option.lastname || ''}`.toLowerCase();
                  const username = (option.username || '').toLowerCase();
                  const searchTerm = inputValue.toLowerCase();
                  return fullName.includes(searchTerm) || username.includes(searchTerm);
                })
              }
              isOptionEqualToValue={(option, value) => option._id === value._id}
              renderInput={(params) => (
                <TextField {...params} label="Select Clients" placeholder="Search by name or username..." />
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                  const name = `${option.firstname || ''} ${option.lastname || ''}`.trim();
                  const label = name ? `${name} (${option.username})` : option.username;
                  return (
                    <Chip {...getTagProps({ index })} key={option._id} label={label} size="small" variant="outlined" />
                  );
                })
              }
            />
          </Grid>{' '}
          {/* Alert Type and Trade Type Filters */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom color="primary">
              Alert & Trade Types
            </Typography>
          </Grid>
          {/* Alert Type Filter */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              size="small"
              options={alertTypes.data}
              loading={alertTypes.isLoading}
              value={alertTypes.data.filter((alertType) => tempFilters.alertType.includes(alertType.type))}
              onChange={(event, newValue) => {
                handleChange(
                  'alertType',
                  newValue.map((item) => item.type)
                );
              }}
              getOptionLabel={(option) => option.type.replace(/_/g, ' ')}
              isOptionEqualToValue={(option, value) => option.type === value.type}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Alert Types"
                  placeholder="Search alert types..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {alertTypes.isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2">{option.type.replace(/_/g, ' ')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.type}
                    label={option.type.replace(/_/g, ' ')}
                    size="small"
                    variant="outlined"
                  />
                ))
              }
            />
          </Grid>
          {/* Trade Type Filter */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              size="small"
              options={TRADE_TYPE_OPTIONS}
              value={TRADE_TYPE_OPTIONS.filter((option) => tempFilters.tradeType.includes(option.value))}
              onChange={(event, newValue) => {
                handleChange(
                  'tradeType',
                  newValue.map((item) => item.value)
                );
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField {...params} label="Select Trade Types" placeholder="Search trade types..." />
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.value}
                    label={option.label}
                    size="small"
                    variant="outlined"
                  />
                ))
              }
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleReset} color="error" size="small">
          Reset All
        </Button>
        <Button onClick={onClose} size="small">
          Cancel
        </Button>
        <Button onClick={handleApply} variant="contained" size="small">
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );
}

FilterDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  onApply: PropTypes.func.isRequired,
  userRole: PropTypes.string.isRequired,
};

// Main component
const DeltaAlertTrades = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { tradeAlerts, isLoading, lastSuspiciousTradeUpdated } = useSelector((state) => state.suspiciousActivity);
  console.log('trade alerts', tradeAlerts);
  const scrollableContainerRef = useRef(null); // Filter states
  const [filters, setFilters] = useState({
    client: [],
    admin: [],
    broker: [],
    script: '',
    alertType: [],
    tradeType: [],
    startTime: '',
    endTime: '',
    startPrice: '',
    endPrice: '',
    duration: '',
    page: 1,
    pageSize: 20,
  });
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [alertTypesModalOpen, setAlertTypesModalOpen] = useState(false);
  const [selectedAlertTypes, setSelectedAlertTypes] = useState([]);
  const navigate = useNavigate();

  // Convert array filters to comma-separated strings for API
  const formatFiltersForAPI = useCallback((filterObj) => {
    const formattedFilters = { ...filterObj }; // Convert arrays to comma-separated strings
    if (formattedFilters.client && Array.isArray(formattedFilters.client)) {
      formattedFilters.client = formattedFilters.client.join(',');
    }
    if (formattedFilters.admin && Array.isArray(formattedFilters.admin)) {
      formattedFilters.admin = formattedFilters.admin.join(',');
    }
    if (formattedFilters.broker && Array.isArray(formattedFilters.broker)) {
      formattedFilters.broker = formattedFilters.broker.join(',');
    }
    if (formattedFilters.alertType && Array.isArray(formattedFilters.alertType)) {
      formattedFilters.alertType = formattedFilters.alertType.join(',');
    }
    if (formattedFilters.tradeType && Array.isArray(formattedFilters.tradeType)) {
      formattedFilters.tradeType = formattedFilters.tradeType.join(',');
    }

    // Convert duration to number if it exists and is valid
    if (formattedFilters.duration && formattedFilters.duration !== '') {
      const durationNum = Number(formattedFilters.duration);
      if (durationNum > 0) {
        formattedFilters.duration = durationNum;
      } else {
        delete formattedFilters.duration;
      }
    }

    // Remove empty fields
    Object.keys(formattedFilters).forEach((key) => {
      if (!formattedFilters[key] || formattedFilters[key] === '') {
        delete formattedFilters[key];
      }
    });

    return formattedFilters;
  }, []); // Load initial data
  useEffect(() => {
    dispatch(clearTradeAlerts());
    const formattedFilters = formatFiltersForAPI(filters);
    dispatch(getTradeAlerts(formattedFilters));
  }, [dispatch, filters, formatFiltersForAPI]);

  // Load alert types on component mount
  useEffect(() => {
    dispatch(getAlertTypes());
  }, [dispatch]);

  // Fetch last suspicious trade updated time on mount
  useEffect(() => {
    dispatch(getLastSuspiciousTradeUpdated());
  }, [dispatch]);

  // Handle filter changes
  const handleFilterChange = useCallback(
    (newFilters) => {
      setFilters({ ...newFilters, page: 1 }); // Reset to page 1 when filters change
      dispatch(clearTradeAlerts());
      const formattedFilters = formatFiltersForAPI({ ...newFilters, page: 1 });
      dispatch(getTradeAlerts(formattedFilters));
    },
    [dispatch, formatFiltersForAPI]
  );

  // Handle page change
  const handlePageChange = useCallback(
    (newPage) => {
      const updatedFilters = { ...filters, page: newPage };
      setFilters(updatedFilters);
      const formattedFilters = formatFiltersForAPI(updatedFilters);
      dispatch(getTradeAlerts(formattedFilters));
    },
    [dispatch, filters, formatFiltersForAPI]
  );
  // Handle view details
  const handleViewDetails = useCallback(
    (alert) => {
      dispatch(setSelectedAlert(alert));
      setDetailDialogOpen(true);
    },
    [dispatch]
  );

  const handleCloseDialog = useCallback(() => {
    setDetailDialogOpen(false);
    dispatch(setSelectedAlert(null));
  }, [dispatch]); // Count applied filters (role-based)
  const getAppliedFiltersCount = () => {
    let count = 0;
    if (filters.client.length > 0) count += 1;

    // Role-based counting
    const userRole = user?.role?.rolename || 'client';
    if (userRole === 'superAdmin' && filters.admin.length > 0) count += 1;
    if ((userRole === 'superAdmin' || userRole === 'admin') && filters.broker.length > 0) count += 1;

    if (filters.script) count += 1;
    if (filters.alertType.length > 0) count += 1;
    if (filters.tradeType.length > 0) count += 1;
    if (filters.startTime) count += 1;
    if (filters.endTime) count += 1;
    if (filters.startPrice) count += 1;
    if (filters.endPrice) count += 1;
    if (filters.duration) count += 1;
    return count;
  }; // Get applied filters display (role-based)  // Handle alert types modal
  const handleShowAllAlertTypes = (alertTypes) => {
    setSelectedAlertTypes(alertTypes);
    setAlertTypesModalOpen(true);
  };

  // Handler to redirect to client profile with trade
  const handleClientProfileWithTradeRedirect = (clientId) => {
    console.log('Redirecting to client profile with trade for clientId:', clientId, user?.role?.rolename);
    const route = getClientProfileWithTradeRoute(user?.role?.rolename, clientId);
    if (route) navigate(route);
  };

  return (
    <Container maxWidth="xl">
      <Box>
        {/* Table with Toolbar */}
        <Card>
          {/* Toolbar */}
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
              <Typography variant="h4" gutterBottom>
                Delta Alert Trades
                {tradeAlerts?.pagination?.totalCount !== undefined && (
                  <>
                    <Typography
                      component="span"
                      variant="subtitle1"
                      color="text.secondary"
                      sx={{ ml: 2, fontWeight: 400 }}
                    >
                      ({tradeAlerts?.pagination?.totalCount})
                    </Typography>
                    {typeof tradeAlerts.totalProfit === 'number' && (
                      <Typography
                        component="span"
                        variant="subtitle1"
                        sx={{ ml: 2, fontWeight: 600, color: 'success.dark' }}
                      >
                        {fIndianCurrency(tradeAlerts.totalProfit)}
                      </Typography>
                    )}
                  </>
                )}
              </Typography>
              {lastSuspiciousTradeUpdated && (
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Last Updated: {fCompactDateTime(lastSuspiciousTradeUpdated)}
                </Typography>
              )}
            </Box>{' '}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Page Size Selector */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Per Page</InputLabel>
                <Select
                  value={filters.pageSize}
                  onChange={(e) => handleFilterChange({ ...filters, page: 1, pageSize: Number(e.target.value) })}
                  label="Per Page"
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>

              {/* Filter Button */}
              <Badge badgeContent={getAppliedFiltersCount()} color="primary" invisible={getAppliedFiltersCount() === 0}>
                <Button
                  variant={getAppliedFiltersCount() > 0 ? 'contained' : 'outlined'}
                  onClick={() => setFilterDialogOpen(true)}
                  startIcon={<FilterListIcon />}
                  color={getAppliedFiltersCount() > 0 ? 'primary' : 'inherit'}
                >
                  Filters
                </Button>
              </Badge>
            </Box>
          </Box>{' '}
          {/* Scrollable Table Container */}
          <StyledTableContainer>
            {' '}
            <Box
              ref={scrollableContainerRef}
              sx={{
                width: '100%',
                height: 'auto', // Let table take natural height
                overflowY: 'visible', // Page-level scrolling for sticky header
                overflowX: 'auto', // Allow horizontal scrolling
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Script</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Last Alert Time</TableCell>
                    <TableCell>Alert Types</TableCell>
                    <TableCell>Profit</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Trade Type</TableCell>
                    <TableCell className="sticky-actions" />
                  </TableRow>
                </TableHead>{' '}
                <TableBody>
                  {tradeAlerts.trades.map((alert) => (
                    <TableRow key={alert._id} hover>
                      {/* User */}
                      <TableCell
                        onClick={() => handleClientProfileWithTradeRedirect(alert.user._id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {alert.user.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {alert.user.firstName} {alert.user.lastName}
                          </Typography>
                        </Box>
                      </TableCell>
                      {/* Script */}
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {alert.trade.script.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {alert.trade.script.tradingsymbol}
                        </Typography>
                      </TableCell>
                      {/* Price (Buy/Sell) */}
                      <TableCell>
                        <Box>
                          {alert.trade.buy && (
                            <Typography variant="body2" color="success.dark" display="block">
                              Buy: {fIndianCurrency(alert.trade.buy)}
                            </Typography>
                          )}
                          {alert.trade.sell && (
                            <Typography variant="body2" color="error.main" display="block">
                              Sell: {fIndianCurrency(alert.trade.sell)}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      {/* Time (Bought/Sold/Created) */}
                      <TableCell>
                        {' '}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {alert.trade.boughtAt && (
                            <Typography variant="body2" color="success.dark">
                              Bought: {fCompactDateTime(alert.trade.boughtAt)}
                            </Typography>
                          )}
                          {alert.trade.soldAt && (
                            <Typography variant="body2" color="error.main">
                              Sold: {fCompactDateTime(alert.trade.soldAt)}
                            </Typography>
                          )}
                          {!alert.trade.boughtAt && !alert.trade.soldAt && (
                            <Typography variant="body2" color="text.secondary">
                              Created: {fCompactDateTime(alert.createdAt || alert.latestAlertTimestamp)}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>{' '}
                      {/* Last Alert Time */}
                      <TableCell>
                        <Typography variant="body2">{fCompactDateTime(alert.latestAlertTimestamp)}</Typography>
                      </TableCell>
                      {/* Alert Types */}
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(() => {
                            // Extract unique alert types from the alerts array
                            const alertTypes = [...new Set(alert.alerts?.map((a) => a.alertType) || [])];
                            return (
                              <>
                                {alertTypes.slice(0, 2).map((alertType, index) => (
                                  <AlertTypeChip key={index} label={alertType} alerttype={alertType} size="small" />
                                ))}
                                {alertTypes.length > 2 && (
                                  <Chip
                                    label={`+${alertTypes.length - 2} more`}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => handleShowAllAlertTypes(alertTypes)}
                                    sx={{ cursor: 'pointer' }}
                                  />
                                )}
                              </>
                            );
                          })()}
                        </Box>
                      </TableCell>
                      {/* Profit */}
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={alert.trade.profit >= 0 ? 'success.dark' : 'error.main'}
                          fontWeight={600}
                        >
                          {fIndianCurrency(alert.trade.profit)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDuration(alert.trade.timeDifference)}
                        </Typography>
                      </TableCell>
                      {/* Trade Type */}
                      <TableCell>
                        <Chip
                          label={getTradeTypeLabel(alert.trade.type)}
                          color={getTradeTypeColor(alert.trade.type)}
                          size="small"
                        />
                      </TableCell>
                      {/* Actions */}
                      <TableCell className="sticky-actions">
                        <IconButton size="small" onClick={() => handleViewDetails(alert)} color="primary">
                          <VisibilityIcon />{' '}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>{' '}
            {isLoading && (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            )}{' '}
          </StyledTableContainer>{' '}
          {/* Pagination */}
          {tradeAlerts.pagination && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 3,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Showing{' '}
                {tradeAlerts.pagination.totalCount > 0
                  ? (tradeAlerts.pagination.currentPage - 1) * tradeAlerts.pagination.pageSize + 1
                  : 0}{' '}
                to{' '}
                {Math.min(
                  tradeAlerts.pagination.currentPage * tradeAlerts.pagination.pageSize,
                  tradeAlerts.pagination.totalCount
                )}{' '}
                of {tradeAlerts.pagination.totalCount} entries
              </Typography>

              {tradeAlerts.pagination.totalPages > 1 && (
                <Pagination
                  count={tradeAlerts.pagination.totalPages}
                  page={tradeAlerts.pagination.currentPage || filters.page}
                  onChange={(event, newPage) => handlePageChange(newPage)}
                  color="primary"
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                />
              )}
            </Box>
          )}
        </Card>
        {/* Alert Detail Dialog */}
        <AlertDetailDialog open={detailDialogOpen} onClose={handleCloseDialog} alert={tradeAlerts.selectedAlert} />{' '}
        {/* Filter Dialog */}
        <FilterDialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          filters={filters}
          onApply={handleFilterChange}
          userRole={user?.role?.rolename || 'client'}
        />{' '}
        {/* Alert Types Modal */}
        <AlertTypesModal
          open={alertTypesModalOpen}
          onClose={() => setAlertTypesModalOpen(false)}
          alertTypes={selectedAlertTypes}
        />
      </Box>
    </Container>
  );
};

export default DeltaAlertTrades;

/*
Example usage: (replace with your actual click handler in the table or UI)
<Button onClick={() => handleClientProfileWithTradeRedirect(clientId)}>Go to Client Profile with Trade</Button>
*/
